/**
 * App switcher (ADR-0015, choice #4) — an Office-365-style waffle in the
 * portal header that lets a signed-in end-user hop between the apps they're a
 * member of without re-authenticating (the token is global).
 *
 * Self-hiding: renders NOTHING when the operator turned the switcher off
 * (`brand.switcher_enabled === false`) OR when the user belongs to a single
 * app (a one-item launcher is pointless). So it's safe to drop into the header
 * unconditionally.
 */
import { useQuery } from "@tanstack/react-query";
import { Check, LayoutGrid } from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useOutsideClick } from "@/hooks/useOutsideClick";
import { portalApi } from "@/lib/api-portal";
import { parentLauncherPath, portalHomePath } from "@/lib/portalAuth";

export function AppSwitcher({ currentSlug }: { currentSlug: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, () => setOpen(false), open);

  // The launcher feed + the operator master-switch (both public-ish, cached).
  const appsQ = useQuery({ queryKey: ["my-apps"], queryFn: () => portalApi.myApps() });
  const brandQ = useQuery({
    queryKey: ["parent-brand"],
    queryFn: () => portalApi.getParentBrand(),
    staleTime: 5 * 60_000,
  });

  const apps = appsQ.data ?? [];
  const enabled = brandQ.data?.switcher_enabled ?? true;

  // Nothing to switch to (single app) or operator disabled it → render nothing.
  if (!enabled || apps.length <= 1) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Switch app"
        aria-expanded={open}
        title="Switch app"
        onClick={() => setOpen((o) => !o)}
        className="rounded-md p-1.5 transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10 focus-visible:outline-none"
      >
        <LayoutGrid className="h-4 w-4" strokeWidth={1.5} />
      </button>

      {open && (
        <div
          role="menu"
          className="portal-pop absolute right-0 z-50 mt-1 w-64 overflow-hidden rounded-[var(--portal-radius)] border border-border bg-surface text-foreground shadow-lg"
        >
          <div className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
            Your apps
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {apps.map((app) => {
              const active = app.slug === currentSlug;
              return (
                <Link
                  key={app.slug}
                  to={portalHomePath(app.slug)}
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-muted"
                >
                  {app.logo_url ? (
                    <img src={app.logo_url} alt="" className="h-6 w-6 shrink-0 rounded object-contain" />
                  ) : (
                    <div
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-semibold text-white"
                      style={{ background: app.theme.accent || "#0f172a" }}
                    >
                      {(app.name.trim()[0] || "?").toUpperCase()}
                    </div>
                  )}
                  <span className="min-w-0 flex-1 truncate">{app.name}</span>
                  {active && <Check className="h-4 w-4 shrink-0 text-muted-foreground" />}
                </Link>
              );
            })}
          </div>
          <Link
            to={parentLauncherPath()}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block border-t border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            All apps…
          </Link>
        </div>
      )}
    </div>
  );
}
